import { Component, OnInit, OnDestroy, HostListener, Output, EventEmitter } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { SignupStateService } from '../../services/signup-state.service';
import { GeolocationService } from '../../services/geolocation.service';
import { CognitoService } from 'src/app/auth/services/cognito.service';
import { PersonValidationService } from 'src/app/auth/services/person-validation.service';
import { LlaveService } from 'src/app/shared/services/llave.service';
import {
  COUNTRY_PHONE_OPTIONS,
  DEFAULT_COUNTRY,
  CountryPhone,
  getCountryByCode,
  detectCountryFromInput,
  normalizePhoneInput,
  phonePatternFor,
  phoneErrorFor,
  phoneHintFor,
  phoneValidatorFor,
  PASOS_COMUNES,
} from '../../constants/signup.constants';
import { passwordRulesValidator } from '../../utils/password-rules';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-registration-form-step',
  templateUrl: './registration-form-step.component.html',
  styleUrls: ['./registration-form-step.component.sass'],
})
export class RegistrationFormStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();

  form!: FormGroup;

  // Password visibility
  showPassword = false;
  showRepeatPassword = false;

  // Phone selector
  countryPhoneOptions = COUNTRY_PHONE_OPTIONS;
  selectedCountry: CountryPhone = DEFAULT_COUNTRY;
  showDropdown = false;

  // El formato del numero (ejemplo, largo maximo y validador) sale de la
  // tabla de paises: cada pais tiene el suyo.
  get selectedCode(): string {
    return this.selectedCountry.code;
  }

  get selectedFlag(): string {
    return `assets/flags/${this.selectedCountry.flag}`;
  }

  get phonePlaceholder(): string {
    return this.selectedCountry.placeholder;
  }

  get phoneHint(): string {
    return phoneHintFor(this.selectedCountry);
  }

  // Geolocation loading
  isLoadingGeo = true;

  // Submit loading
  isSubmitting = false;

  /**
   * Estado del chequeo de la llave contra el servidor.
   *
   * `idle` mientras el formato todavia no da; recien cuando pasa el validador
   * local se consulta, para no gastar una llamada por cada tecla.
   */
  llaveEstado: 'idle' | 'verificando' | 'libre' | 'tomada' = 'idle';
  llaveMensaje = '';

  /**
   * Si el programa de referidos esta encendido.
   *
   * Arranca en `false` y no en `true`: hasta saberlo, el campo no se muestra.
   * Al reves, el usuario veria aparecer y desaparecer un campo obligatorio.
   */
  referidosActivo = false;

  private llaveInput$ = new Subject<string>();

  private subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    public signupState: SignupStateService,
    private geoService: GeolocationService,
    private cognitoService: CognitoService,
    private personValidationService: PersonValidationService,
    private llaveService: LlaveService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.detectCountryByIp();
    this.escucharLlave();
    this.resolverSiPideLlave();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private initForm(): void {
    // Si hay datos previos, restaurarlos
    const savedData = this.signupState.currentState.formData;

    this.form = this.fb.group({
      email: [savedData?.email || '', [Validators.required, Validators.email]],
      // La llave del usuario. Nace SIN validadores: se los pone
      // `resolverSiPideLlave` sólo si el programa está encendido, porque con
      // el programa apagado el campo no se muestra y un `required` invisible
      // dejaría el formulario imposible de enviar.
      llave: [savedData?.llave || ''],
      // Los requisitos los valida passwordRulesValidator (mismas reglas que el
      // checklist que ve el usuario). El campo repetir solo exige que este
      // completo y que coincida: los requisitos ya los verifica el primero.
      password: [
        savedData?.password || '',
        [Validators.required, passwordRulesValidator],
      ],
      repeatPassword: [savedData?.repeatPassword || '', [Validators.required]],
      // El campo guarda lo que escribe el usuario (`+54 9 11 2345-6789`); el
      // validador lo juzga normalizado y `commitPhone` lo deja en digitos.
      phone: [
        savedData?.phone || '',
        [Validators.required, phoneValidatorFor(() => this.selectedCountry)],
      ],
      phoneCode: [savedData?.phoneCode || DEFAULT_COUNTRY.code, Validators.required],
      termsAccepted: [savedData?.termsAccepted || false, Validators.requiredTrue],
      marketingAccepted: [savedData?.marketingAccepted || false],
    });

    // Restaurar país seleccionado si hay datos previos
    if (savedData?.phoneCode) {
      this.selectedCountry = getCountryByCode(savedData.phoneCode);
    }

    // Validador de contraseñas coincidentes
    this.form.setValidators(this.passwordsMatchValidator);

    // Re-validar cuando cambia repeatPassword
    const repeatSub = this.form.get('repeatPassword')?.valueChanges.subscribe(() => {
      this.form.updateValueAndValidity({ onlySelf: false });
    });
    if (repeatSub) this.subscriptions.push(repeatSub);
  }

  /**
   * Detecta el país por IP y auto-selecciona el código de teléfono
   */
  private detectCountryByIp(): void {
    // Si ya hay datos guardados, no sobrescribir
    if (this.signupState.currentState.formData?.phoneCode) {
      this.isLoadingGeo = false;
      return;
    }

    this.geoService.getGeolocation().pipe(take(1)).subscribe({
      next: (geoData) => {
        if (geoData) {
          const country = geoData.detectedCountry;
          this.setCountry(country);

          // Guardar geoData en el estado para uso futuro (OCR, etc.)
          this.signupState.setGeoData({
            countryCode: geoData.countryCode,
            countryName: geoData.countryName,
            city: geoData.city,
            ip: geoData.ip,
            detectedCountryIso: country.iso,
          });
        }
        this.isLoadingGeo = false;
      },
      error: () => {
        this.isLoadingGeo = false;
      },
    });
  }

  /**
   * Establece el país seleccionado
   */
  private setCountry(country: CountryPhone): void {
    this.selectedCountry = country;
    this.form.get('phoneCode')?.setValue(country.code);
    // El validador lee el pais en cada pasada: hay que re-validar lo ya
    // escrito, si no queda juzgado con las reglas del pais anterior. No se
    // recorta nada: si no calza, se marca y el usuario lo ve.
    this.form.get('phone')?.updateValueAndValidity();
  }

  private passwordsMatchValidator(form: AbstractControl) {
    const password = form.get('password')?.value;
    const repeatPassword = form.get('repeatPassword')?.value;
    return password === repeatPassword ? null : { mismatch: true };
  }

  get isFormValid(): boolean {
    return this.form.valid && this.form.get('termsAccepted')?.value;
  }

  /**
   * Valor actual de la contraseña: alimenta el checklist de requisitos
   */
  get passwordValue(): string {
    return this.form?.get('password')?.value || '';
  }

  /**
   * La contraseña cumple todos los requisitos (check verde en el campo)
   */
  get isPasswordComplete(): boolean {
    const control = this.form?.get('password');
    return !!control && !control.errors && !!this.passwordValue;
  }

  /**
   * Las dos contraseñas coinciden (check verde en "repetir contraseña")
   */
  get passwordsMatch(): boolean {
    const repeatPassword = this.form?.get('repeatPassword')?.value || '';
    return !!repeatPassword && repeatPassword === this.passwordValue;
  }

  async onSubmit(): Promise<void> {
    this.commitPhone();
    if (!this.form.valid || this.isSubmitting) return;
    if (this.referidosActivo && this.llaveEstado === 'tomada') {
      this.onError.emit('Ese usuario ya est\u00e1 tomado. Eleg\u00ed otro.');
      return;
    }

    this.isSubmitting = true;

    try {
      const email = this.form.get('email')?.value?.toLowerCase();
      const password = this.form.get('password')?.value;
      const isCompany = this.signupState.isEmpresa ? 1 : 0;

      // 0. Verificar si el email cambió respecto al registro en progreso
      const savedEmail = this.signupState.currentState.formData?.email?.toLowerCase();
      const emailChanged = savedEmail && savedEmail !== email;

      if (emailChanged) {
        // El usuario cambió el email - iniciar nuevo registro
        // Cerrar cualquier sesión de Cognito existente
        try {
          await this.cognitoService.signOut();
        } catch (e) {
          // Ignorar error si no hay sesión activa
        }
        // Limpiar el estado completamente
        this.signupState.reset();
        this.signupState.setAccountType(isCompany ? 'empresas' : 'personal');
        // Establecer el paso en 2 (formulario de registro) para que nextStep() vaya al 3
        this.signupState.setStep(PASOS_COMUNES.FORMULARIO);
      }

      // Siempre resetear flags de verificación al re-enviar el formulario
      // para forzar re-validación de email y teléfono
      this.signupState.setEmailVerified(false);
      this.signupState.setPhoneVerified(false);

      // Guardar datos en el estado
      this.signupState.setFormData(this.form.value);

      // 1. Validación de email para personas
      if (this.signupState.isPersonal) {
        const validation = await this.personValidationService.validatePersonEmail(email);
        if (!validation.isAvailable) {
          this.onError.emit(validation.message || 'Este correo ya está registrado.');
          this.isSubmitting = false;
          return;
        }
      }

      // 2. Mostrar confirmación
      const confirmed = await this.showConfirmationDialog(email);
      if (!confirmed) {
        this.isSubmitting = false;
        return;
      }

      // 3. Registrar en Cognito
      await this.cognitoService.signUp(email, password, isCompany, 'aguacate_client');

      // 4. Guardar timestamp de envío OTP para restaurar countdown
      this.signupState.setEmailOtpSentAt(Date.now());

      // 5. Apartar la llave contra este correo.
      //
      // Va despues del alta en Cognito y no antes: si el registro se cae en el
      // paso anterior, no queda una llave bloqueada 48 horas por alguien que
      // nunca llego a existir.
      //
      // No frena el registro si falla. La llave se puede volver a elegir desde
      // el modulo despues de entrar, mientras que un alta perdida no se
      // recupera.
      if (this.llaveService.validarFormato(this.llaveNormalizada).valida) {
        try {
          await this.llaveService.reservar(this.llaveNormalizada, email);
        } catch (e) {
          console.warn('No se pudo apartar la llave; el registro sigue.', e);
        }
      }

      // 6. Avanzar al paso de verificación de email
      await this.signupState.nextStep();

    } catch (error: any) {
      this.handleSignupError(error);
    } finally {
      this.isSubmitting = false;
    }
  }

  private async showConfirmationDialog(email: string): Promise<boolean> {
    const result = await Swal.fire({
      title: 'Confirmar registro',
      html: `
        <p>Se enviará un código de verificación a:</p>
        <p><strong>${email}</strong></p>
        <p>¿Es correcto?</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, continuar',
      cancelButtonText: 'Corregir',
      confirmButtonColor: '#0e7705',
      cancelButtonColor: '#6c757d',
    });

    return result.isConfirmed;
  }

  /**
   * Le ofrece continuar el registro que dejo a medias, en vez de mandarlo a
   * inventar otro correo.
   *
   * Se llega aca cuando Cognito dice que el correo existe Y el reenvio del
   * codigo falla, o sea cuando el usuario ya esta confirmado: abandono despues
   * del paso 3. Su registro esta empezado del lado del backend, asi que la
   * salida es iniciar sesion — el guard de registro en progreso lo devuelve al
   * paso donde quedo.
   */
  private async ofrecerContinuarRegistro(): Promise<void> {
    const result = await Swal.fire({
      title: 'Ya empezaste un registro',
      html: `
        <p>Ya empezaste un registro con este correo.</p>
        <p>Inicia sesion para continuar donde lo dejaste.</p>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: 'Iniciar sesion',
      cancelButtonText: 'Usar otro correo',
      confirmButtonColor: '#0e7705',
      cancelButtonColor: '#6c757d',
    });

    if (result.isConfirmed) {
      this.router.navigate(['/auth/login']);
      return;
    }

    // Eligio cambiar de correo: se limpia el campo y vuelve el foco al form.
    this.form.get('email')?.setValue('');
    this.form.get('email')?.markAsUntouched();
  }

  /** La llave lista para mandar: sin `@`, sin espacios y en mayuscula. */
  get llaveNormalizada(): string {
    return this.llaveService.normalizar(this.form?.get('llave')?.value || '');
  }

  /** Lo que el usuario va a compartir, para mostrarlo mientras escribe. */
  get llavePreview(): string {
    const llave = this.llaveNormalizada;
    return llave ? '@' + llave : '';
  }

  onLlaveInput(): void {
    const control = this.form.get('llave');
    if (!control) return;

    // Se normaliza en el propio control para que lo que se ve sea lo que se
    // guarda: si el usuario escribe minusculas, las ve pasar a mayuscula.
    const normalizada = this.llaveService.normalizar(control.value || '');
    if (control.value !== normalizada) {
      control.setValue(normalizada, { emitEvent: false });
    }

    const formato = this.llaveService.validarFormato(normalizada);
    if (!formato.valida) {
      this.llaveEstado = 'idle';
      this.llaveMensaje = normalizada ? formato.mensaje || '' : '';
      return;
    }

    this.llaveEstado = 'verificando';
    this.llaveMensaje = '';
    this.llaveInput$.next(normalizada);
  }

  /**
   * Consulta la disponibilidad con freno entre teclas.
   *
   * `debounceTime` evita una llamada por caracter y `distinctUntilChanged`
   * evita repetir la misma consulta cuando el usuario borra y reescribe lo
   * mismo. El correo viaja para que el servidor no le marque como tomada su
   * propia reserva a quien vuelve al formulario.
   */
  private escucharLlave(): void {
    const sub = this.llaveInput$
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(async (llave) => {
        const email = this.form.get('email')?.value?.toLowerCase();
        const resultado = await this.llaveService.consultarDisponibilidad(llave, email);

        // El usuario siguio escribiendo mientras viajaba la respuesta: esta
        // llegando tarde y corresponde a otra llave.
        if (this.llaveNormalizada !== llave) return;

        this.llaveEstado = resultado.disponible ? 'libre' : 'tomada';
        this.llaveMensaje = resultado.disponible ? '' : resultado.mensaje || '';
      });

    this.subscriptions.push(sub);
  }

  /**
   * Decide si el registro EXIGE la llave, segun el estado del programa.
   *
   * El campo se muestra siempre: el usuario Aguacate es identidad y elegirlo
   * durante el alta es mas barato que volver despues desde un modal. Lo que
   * cambia es la exigencia — con el programa apagado es opcional, porque
   * frenar un alta por un campo de una funcion que todavia no paga es perder
   * un cliente por nada.
   *
   * Con el programa apagado el campo NO lleva validadores, ni siquiera de
   * formato. El boton de registro es `[disabled]="!form.valid"`, asi que un
   * validador aca no marca un campo: mata el alta entera. Alguien que escribe
   * medio usuario y sigue de largo se quedaba con el boton muerto por un campo
   * opcional. Lo que se escribio a medias se ignora al enviar, y el mensaje
   * bajo el campo igual le dice que le faltan caracteres.
   */
  private async resolverSiPideLlave(): Promise<void> {
    this.referidosActivo = await this.llaveService.programaActivo();

    const control = this.form.get('llave');
    control?.setValidators(
      this.referidosActivo
        ? [Validators.required, Validators.pattern(/^[A-Za-z0-9]{8,10}$/)]
        : [],
    );
    control?.updateValueAndValidity();
  }

  private async handleSignupError(error: any): Promise<void> {
    const errorName = error?.name || error?.code || '';
    const errorMessage = error?.message || '';

    if (errorName === 'UsernameExistsException') {
      // Intentar reenviar código: si el usuario existe pero no fue confirmado,
      // Cognito permite reenviar el código de confirmación
      try {
        const email = this.form.get('email')?.value?.toLowerCase();
        await this.cognitoService.resendConfirmationCode(email);
        // El usuario existe pero no está confirmado — avanzar al paso de OTP
        this.signupState.setEmailOtpSentAt(Date.now());
        await this.signupState.nextStep();
        return;
      } catch (resendError: any) {
        // El reenvío falla cuando el usuario de Cognito ya está confirmado, o sea
        // cuando abandonó DESPUÉS del paso 3. Su registro está a medias y el
        // correo es suyo: decirle "intenta con otro" es un consejo incorrecto y
        // lo deja sin salida. El camino correcto es iniciar sesión y continuar.
        await this.ofrecerContinuarRegistro();
      }
      return;
    } else if (errorName === 'InvalidPasswordException') {
      this.onError.emit('La contraseña no cumple con los requisitos de seguridad.');
    } else if (errorName === 'InvalidParameterException') {
      if (errorMessage.includes('email')) {
        this.onError.emit('El formato del correo no es válido.');
      } else {
        this.onError.emit('Los datos ingresados no son válidos.');
      }
    } else if (errorName === 'LimitExceededException') {
      this.onError.emit('Demasiados intentos. Espera un momento antes de reintentar.');
    } else {
      this.onError.emit('Error al registrar. Intenta nuevamente.');
    }
  }

  async goBack(): Promise<void> {
    // Guardar datos antes de volver
    this.commitPhone();
    if (this.form.value) {
      this.signupState.setFormData(this.form.value);
    }
    await this.signupState.prevStep();
  }

  // Phone selector
  selectPhone(country: CountryPhone): void {
    this.setCountry(country);
    this.showDropdown = false;
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const clickedInside = target.closest('.phone-group');
    if (!clickedInside) {
      this.showDropdown = false;
    }
  }

  // Input helpers - Prevención de espacios
  preventSpaces(event: KeyboardEvent): void {
    if (event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
    }
  }

  // Prevenir pegar completamente (para contraseñas)
  preventPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  // Pegar sin espacios (para email)
  onPasteNoSpaces(event: ClipboardEvent, field: string): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const cleanText = pastedText.replace(/\s/g, '');
    this.form.get(field)?.setValue(cleanText);
  }

  // ==================== Teléfono ====================
  //
  // Nadie tiene que adivinar el formato: el campo acepta el número como lo
  // escriba o pegue la persona (`+54 9 11 2345-6789`, `011 15 2345-6789`,
  // `9 11 2345 6789`...). No se bloquean teclas ni se recorta mientras
  // escribe; se limpia al salir del campo y al enviar.

  // Cada tecla, autocompletado o portapapeles del teclado del celular (que
  // no disparan `paste`). Solo saca letras, y si el número trae `+54` cambia
  // la bandera sola.
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const allowed = input.value.replace(/[^\d+\s().-]/g, '');
    if (allowed !== input.value) {
      this.form.get('phone')?.setValue(allowed);
    }
    this.followCountryOf(allowed);
  }

  // Si lo pegado es un número completo, reemplaza lo que hubiera en el campo
  // (pegar sobre un número viejo no tiene que dar uno mezclado).
  onPhonePaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') || '';
    this.followCountryOf(pasted);
    const digits = normalizePhoneInput(pasted, this.selectedCountry);
    if (phonePatternFor(this.selectedCountry).test(digits)) {
      event.preventDefault();
      this.form.get('phone')?.setValue(digits);
      this.form.get('phone')?.markAsTouched();
    }
  }

  onPhoneBlur(): void {
    this.commitPhone();
  }

  private followCountryOf(raw: string): void {
    const detected = detectCountryFromInput(raw);
    if (detected && detected !== this.selectedCountry) {
      this.setCountry(detected);
    }
  }

  // Deja en el campo los dígitos que se guardan, si el número es válido.
  // Si no lo es, se deja como está para que el usuario vea qué escribió.
  private commitPhone(): void {
    const control = this.form.get('phone');
    const raw: string = control?.value || '';
    this.followCountryOf(raw);
    const digits = normalizePhoneInput(raw, this.selectedCountry);
    if (digits !== raw && phonePatternFor(this.selectedCountry).test(digits)) {
      control?.setValue(digits);
    }
  }

  trackByCode(index: number, country: CountryPhone): string {
    return country.code;
  }

  // Error helpers para errores inline
  getFieldError(field: string): string {
    const control = this.form.get(field);
    if (!control || !control.touched) return '';

    if (field === 'repeatPassword' && this.form.errors?.['mismatch']) {
      return 'Las contraseñas no coinciden.';
    }

    if (field === 'password' && control.errors) {
      if (control.errors['required']) {
        return 'La contraseña es obligatoria.';
      }
      // Los requisitos que faltan no se repiten aca: los muestra en vivo el
      // checklist debajo del campo
      if (control.errors['passwordRules']) {
        return '';
      }
    }

    if (field === 'phone' && control.errors?.['pattern']) {
      return phoneErrorFor(this.selectedCountry, control.value || '');
    }

    if (control.invalid) {
      const messages: Record<string, string> = {
        email: 'Ingresa un correo válido.',
        repeatPassword: 'Debes repetir la contraseña.',
        phone: 'Ingresa tu número de WhatsApp.',
      };
      return messages[field] || 'Campo no válido.';
    }

    return '';
  }

  hasFieldError(field: string): boolean {
    return this.getFieldError(field) !== '';
  }
}
